import React, { useState, useRef, useEffect } from 'react';
import { 
  ExploreConfig, 
  AnalysisMode, 
  AIPlatform, 
  AnalyticsEvent,
  AnalyticsEventType,
  AnalyticsPayload
} from '@explore-with-ai/core';
import { PromptBuilder, PlatformAdapter } from '@explore-with-ai/core';
import './ExploreWithAI.css';

interface ExploreWithAIProps {
  config: ExploreConfig;
  onAnalytics?: (event: any) => void;
  className?: string;
}

const ExploreWithAI: React.FC<ExploreWithAIProps> = ({ 
  config, 
  onAnalytics,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMode, setSelectedMode] = useState<AnalysisMode>('general');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingAction, setPendingAction] = useState<{
    type: 'launch' | 'copy';
    platform?: AIPlatform;
    prompt: string;
  } | null>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const promptBuilder = new PromptBuilder(config);

  // Available modes and platforms based on config
  const availableModes = promptBuilder.getAvailableModes();
  const availablePlatforms = PlatformAdapter.filterEnabledPlatforms(config.enabledPlatforms);
  const buttonStyle = config.buttonStyle || 'default';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAnalytics = (eventType: AnalyticsEventType, payload?: AnalyticsPayload) => {
    const analyticsEvent: AnalyticsEvent = {
      event: eventType,
      sessionId: `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      properties: payload || {},
      timestamp: Date.now(),
      configId: config.id
    };
    
    onAnalytics?.(analyticsEvent);
  };

  const handlePlatformSelect = (platform: AIPlatform) => {
    const prompt = promptBuilder.buildPrompt(selectedMode, config.audience);
    
    setPendingAction({
      type: 'launch',
      platform,
      prompt
    });
    setShowConfirmation(true);
    setIsOpen(false);
    
    handleAnalytics('explore_ai_platform_selected', {
      platform,
      mode: selectedMode,
      datasetId: config.id
    });
  };

  const handleCopyPrompt = () => {
    const prompt = promptBuilder.buildPrompt(selectedMode, config.audience);
    
    setPendingAction({
      type: 'copy',
      prompt
    });
    setShowConfirmation(true);
    setIsOpen(false);
    
    handleAnalytics('explore_ai_copy_prompt', {
      mode: selectedMode,
      datasetId: config.id
    });
  };

  const executeAction = async () => {
    if (!pendingAction) return;

    try {
      if (pendingAction.type === 'launch' && pendingAction.platform) {
        PlatformAdapter.launchPlatform(pendingAction.platform, pendingAction.prompt);
        handleAnalytics('explore_ai_launch', {
          platform: pendingAction.platform, 
          mode: selectedMode,
          datasetId: config.id
        });
      } else if (pendingAction.type === 'copy') {
        await PlatformAdapter.copyToClipboard(pendingAction.prompt);
        handleAnalytics('explore_ai_copy_prompt', {
          mode: selectedMode,
          datasetId: config.id
        });
      }
    } catch (error) {
      console.error('Error executing action:', error);
      // Log error but don't send analytics for now since we don't have an error event type
    }

    setShowConfirmation(false);
    setPendingAction(null);
  };

  const getActionDescription = () => {
    if (!pendingAction) return '';

    if (pendingAction.type === 'launch' && pendingAction.platform) {
      const platformName = PlatformAdapter.getPlatformName(pendingAction.platform);
      const supportsPrefill = PlatformAdapter.supportsPrefill(pendingAction.platform);
      
      if (supportsPrefill) {
        return `Launch ${platformName} with the generated prompt pre-filled and ready for analysis.`;
      } else {
        return `Open ${platformName} and copy the generated prompt to your clipboard for pasting.`;
      }
    } else if (pendingAction.type === 'copy') {
      return 'Copy the generated prompt to your clipboard so you can paste it into any AI platform.';
    }

    return '';
  };

  return (
    <div className={`explore-with-ai ${className}`} ref={dropdownRef}>
      <button
        className={`explore-with-ai__button explore-with-ai__button--${buttonStyle}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <svg className="explore-with-ai__icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
        Explore with AI
        <svg className={`explore-with-ai__chevron ${isOpen ? 'explore-with-ai__chevron--open' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="explore-with-ai__dropdown">
          <div className="explore-with-ai__section">
            <label className="explore-with-ai__label">Analysis Mode</label>
            <select
              value={selectedMode}
              onChange={(e) => setSelectedMode(e.target.value as AnalysisMode)}
              className="explore-with-ai__select"
            >
              {availableModes.map((mode: string) => (
                <option key={mode} value={mode}>
                  {mode.charAt(0).toUpperCase() + mode.slice(1)} Analysis
                </option>
              ))}
            </select>
          </div>

          <div className="explore-with-ai__section">
            <label className="explore-with-ai__label">AI Platform</label>
            <div className="explore-with-ai__platforms">
              {availablePlatforms.map((platform: AIPlatform) => (
                <button
                  key={platform}
                  onClick={() => handlePlatformSelect(platform)}
                  className="explore-with-ai__platform"
                >
                  {PlatformAdapter.getPlatformName(platform)}
                </button>
              ))}
            </div>
          </div>

          <div className="explore-with-ai__section">
            <button
              onClick={handleCopyPrompt}
              className="explore-with-ai__copy"
            >
              <svg className="explore-with-ai__copy-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Copy Prompt
            </button>
          </div>
        </div>
      )}

      {showConfirmation && (
        <div className="explore-with-ai__modal">
          <div className="explore-with-ai__modal-content">
            <h3 className="explore-with-ai__modal-title">Confirm Action</h3>
            <p className="explore-with-ai__modal-description">
              {getActionDescription()}
            </p>
            
            <div className="explore-with-ai__modal-disclaimers">
              <div className="explore-with-ai__disclaimer">
                <strong>⚠️ Third-Party Site:</strong> You are about to visit a third-party AI platform. Please review their terms of service and privacy policy.
              </div>
              <div className="explore-with-ai__disclaimer">
                <strong>🤖 AI Limitations:</strong> AI models can make mistakes or generate inaccurate information. Always verify important details and use critical thinking when reviewing AI responses.
              </div>
            </div>
            <div className="explore-with-ai__modal-actions">
              <button
                onClick={() => {
                  setShowConfirmation(false);
                  setPendingAction(null);
                }}
                className="explore-with-ai__modal-cancel"
              >
                Cancel
              </button>
              <button
                onClick={executeAction}
                className="explore-with-ai__modal-confirm"
              >
                {pendingAction?.type === 'launch' ? 'Launch' : 'Copy'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExploreWithAI;
